import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API, formatApiError } from "@/App";
import { useVideoUrl } from "@/hooks/useVideoUrl";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  Check,
  Crop,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Plus,
  Minus,
  Loader2,
  Save,
  AlertCircle,
  Info,
  ChevronDown,
  GripVertical,
  Eye,
  Layers,
  Download,
  Scissors,
  Link2,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import TimelineTrack from "@/components/timeline/TimelineTrack";
import PreviewPlayer from "@/components/preview/PreviewPlayer";
import { ContinueStep } from "@/components/crop/ContinueStep";

const STEPS = [
  { id: 1, label: "Project Setup" },
  { id: 2, label: "Assets" },
  { id: 3, label: "Crop Workspace" },
  { id: 4, label: "Continue" },
];

const DEFAULT_ASPECTS = [
  { id: "1:1", label: "1:1", desc: "Square" },
  { id: "9:16", label: "9:16", desc: "Vertical / Stories" },
  { id: "4:5", label: "4:5", desc: "Instagram Feed" },
];

const sortByTime = (arr) => [...arr].sort((a, b) => a.start_time - b.start_time);

// Interpolate crop position between keyframes at a given time
const interpolateKeyframes = (keyframes, time) => {
  if (!keyframes || keyframes.length === 0) return null;
  if (keyframes.length === 1) return { x: keyframes[0].x, y: keyframes[0].y };
  const sorted = [...keyframes].sort((a, b) => a.time - b.time);
  if (time <= sorted[0].time) return { x: sorted[0].x, y: sorted[0].y };
  if (time >= sorted[sorted.length - 1].time)
    return { x: sorted[sorted.length - 1].x, y: sorted[sorted.length - 1].y };
  for (let i = 0; i < sorted.length - 1; i++) {
    if (time >= sorted[i].time && time <= sorted[i + 1].time) {
      const t =
        (time - sorted[i].time) / (sorted[i + 1].time - sorted[i].time);
      return {
        x: sorted[i].x + (sorted[i + 1].x - sorted[i].x) * t,
        y: sorted[i].y + (sorted[i + 1].y - sorted[i].y) * t,
      };
    }
  }
  return { x: sorted[sorted.length - 1].x, y: sorted[sorted.length - 1].y };
};

const KEYFRAME_TOLERANCE = 0.05; // 50ms tolerance for "at" a keyframe

const CropWorkspacePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Step state
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Project Setup
  const [projectMode, setProjectMode] = useState(null); // "existing" | "new"
  const [existingProjects, setExistingProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Step 2: Assets
  const [selectedAspects, setSelectedAspects] = useState([]);
  const [customAspect, setCustomAspect] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Working project
  const [project, setProject] = useState(null);
  const [creatingProject, setCreatingProject] = useState(false);

  // Step 3: Workspace
  const [scenes, setScenes] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStartTime, setAnalysisStartTime] = useState(null);
  const [currentScene, setCurrentScene] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeAspect, setActiveAspect] = useState(null);
  const [cropPositions, setCropPositions] = useState({});
  const [savingCrops, setSavingCrops] = useState(false);
  const [editingScene, setEditingScene] = useState(null);
  // Overlay/graphics state
  const [overlays, setOverlays] = useState([]);
  const [selectedOverlay, setSelectedOverlay] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [reviewLink, setReviewLink] = useState(null);

  // Load existing projects
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/projects`);
        setExistingProjects(res.data);
      } catch {}
      setLoadingProjects(false);
    };
    load();

    // Check if coming from a project
    const pid = searchParams.get("project");
    if (pid) {
      setSelectedProjectId(pid);
      setProjectMode("existing");
      loadProject(pid);
    }
  }, []);

  const loadProject = async (pid) => {
    try {
      const res = await axios.get(`${API}/projects/${pid}`);
      setProject(res.data);
      if (res.data.aspect_ratios?.length) {
        setSelectedAspects(res.data.aspect_ratios);
        setActiveAspect(res.data.aspect_ratios[0]);
      }
      if (res.data.video_duration) setDuration(res.data.video_duration);
      // Resume polling if analysis is in progress
      if (res.data.analysis_status === "processing") {
        setCurrentStep(3);
        setAnalyzing(true);
        setAnalysisProgress(res.data.analysis_progress || 0);
        setAnalysisStartTime(Date.now());
        const pollResume = async () => {
          try {
            const pr = await axios.get(`${API}/projects/${pid}/analysis-progress`);
            setAnalysisProgress(pr.data.progress || 0);
            if (pr.data.status === "completed" && pr.data.scenes) {
              setScenes(sortByTime(pr.data.scenes));
              setDuration(pr.data.duration || 0);
              setAnalysisComplete(true);
              setAnalyzing(false);
              const positions = {};
              pr.data.scenes.forEach((s) => { positions[s.id] = {}; const sub = s.subjects?.[0]; const cx = sub?.position?.x ?? 0.5; const cy = sub?.position?.y ?? 0.5; (res.data.aspect_ratios || []).forEach((ar) => { positions[s.id][ar] = { x: Math.round(cx * 100), y: Math.round(cy * 100) }; }); });
              setCropPositions(positions);
              toast.success(`Analysis complete: ${pr.data.scenes.length} scenes`);
              return;
            }
            if (pr.data.status === "failed") { toast.error(pr.data.error || "Analysis failed"); setAnalyzing(false); return; }
            setTimeout(pollResume, 1500);
          } catch { setTimeout(pollResume, 2000); }
        };
        setTimeout(pollResume, 1000);
        return;
      }
      if (res.data.source_video && res.data.analysis_status === "completed") {
        const scenesRes = await axios.get(`${API}/projects/${pid}/scenes`);
        setScenes(sortByTime(scenesRes.data));
        setAnalysisComplete(true);
        setCurrentStep(3);
        // Load saved per-scene crop positions from DB
        try {
          const cropRes = await axios.get(`${API}/projects/${pid}/crop-positions`);
          const positions = {};
          const aspects = res.data.aspect_ratios || [];
          // Initialize all scenes with defaults first
          (scenesRes.data || []).forEach((s) => {
            positions[s.id] = {};
            aspects.forEach((ar) => {
              positions[s.id][ar] = { x: 50, y: 50 };
            });
          });
          // Overlay saved positions (with keyframes)
          (cropRes.data || []).forEach((cp) => {
            if (!positions[cp.scene_id]) positions[cp.scene_id] = {};
            positions[cp.scene_id][cp.aspect_ratio] = {
              x: Math.round((cp.center_x ?? 0.5) * 100),
              y: Math.round((cp.center_y ?? 0.5) * 100),
              keyframes: (cp.keyframes || []).map((kf) => ({
                time: kf.time,
                x: Math.round((kf.x ?? 0.5) * 100),
                y: Math.round((kf.y ?? 0.5) * 100),
              })),
            };
          });
          setCropPositions(positions);
        } catch {
          // If crop positions fail to load, initialize from scene subjects
          const positions = {};
          (scenesRes.data || []).forEach((s) => {
            positions[s.id] = {};
            const sub = s.subjects?.[0];
            const cx = sub?.position?.x ?? 0.5;
            const cy = sub?.position?.y ?? 0.5;
            (res.data.aspect_ratios || []).forEach((ar) => {
              positions[s.id][ar] = { x: Math.round(cx * 100), y: Math.round(cy * 100) };
            });
          });
          setCropPositions(positions);
        }
        // Load overlays/graphics for this project
        try {
          const gRes = await axios.get(`${API}/projects/${pid}/graphics`);
          setOverlays(gRes.data || []);
        } catch {}
      } else if (res.data.source_video) {
        // If analysis failed previously, reset so user can retry
        if (res.data.analysis_status === "failed") {
          setAnalyzing(false);
          setAnalysisComplete(false);
        }
        setCurrentStep(3);
      } else if (res.data.aspect_ratios?.length > 0) {
        setCurrentStep(2);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Create or select project
  const handleProjectSetup = async () => {
    if (projectMode === "new") {
      if (!newProjectTitle.trim()) { toast.error("Enter a project title"); return; }
      setCreatingProject(true);
      try {
        const res = await axios.post(`${API}/projects`, {
          name: newProjectTitle,
          description: editTitle || "",
        });
        setProject(res.data);
        setSelectedProjectId(res.data.id);
        toast.success("Project created");
        setCurrentStep(2);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setCreatingProject(false); }
    } else {
      if (!selectedProjectId) { toast.error("Select a project"); return; }
      await loadProject(selectedProjectId);
      setCurrentStep(2);
    }
  };

  // Aspect ratio management
  const toggleAspect = (id) => {
    setSelectedAspects((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const addCustomAspect = () => {
    if (!customAspect.match(/^\d+:\d+$/)) {
      toast.error("Use format W:H (e.g. 3:2)");
      return;
    }
    if (!selectedAspects.includes(customAspect)) {
      setSelectedAspects([...selectedAspects, customAspect]);
    }
    setCustomAspect("");
  };

  // Upload video
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!project) { toast.error("Set up project first"); return; }

    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(
        `${API}/projects/${project.id}/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) =>
            setUploadProgress(Math.round((e.loaded * 100) / e.total)),
        }
      );
      toast.success("Video uploaded successfully");
      // Refresh project
      const pRes = await axios.get(`${API}/projects/${project.id}`);
      setProject(pRes.data);
      if (pRes.data.video_duration) setDuration(pRes.data.video_duration);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Save aspect ratios and proceed
  const handleAssetsNext = async () => {
    if (selectedAspects.length === 0) {
      toast.error("Select at least one aspect ratio");
      return;
    }
    if (!project?.source_video) {
      toast.error("Upload a video first");
      return;
    }
    try {
      await axios.post(`${API}/projects/${project.id}/aspect-ratios`, {
        project_id: project.id,
        aspect_ratios: selectedAspects,
        mode: "automated",
      });
      // Refresh
      const pRes = await axios.get(`${API}/projects/${project.id}`);
      setProject(pRes.data);
      if (!activeAspect) setActiveAspect(selectedAspects[0]);
      setCurrentStep(3);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // AI Analysis — background task with progress polling
  const runAnalysis = async () => {
    setAnalyzing(true);
    setAnalysisProgress(0);
    setAnalysisStartTime(Date.now());
    try {
      await axios.post(`${API}/projects/${project.id}/analyze`);
      // Poll for progress
      const poll = async () => {
        try {
          const res = await axios.get(`${API}/projects/${project.id}/analysis-progress`);
          const { status, progress, scenes: resultScenes, duration: dur, error } = res.data;
          setAnalysisProgress(progress || 0);

          if (status === "completed" && resultScenes) {
            setScenes(sortByTime(resultScenes));
            setDuration(dur || 0);
            setAnalysisComplete(true);
            setAnalyzing(false);
            // Init crop positions from AI subjects
            const positions = {};
            resultScenes.forEach((s) => {
              positions[s.id] = {};
              const sub = s.subjects?.[0];
              const cx = sub?.position?.x ?? 0.5;
              const cy = sub?.position?.y ?? 0.5;
              selectedAspects.forEach((ar) => {
                positions[s.id][ar] = { x: Math.round(cx * 100), y: Math.round(cy * 100) };
              });
            });
            setCropPositions(positions);
            toast.success(`Analysis complete: ${resultScenes.length} scenes detected`);
            return;
          }
          if (status === "failed") {
            toast.error(error || "Analysis failed");
            setAnalyzing(false);
            setAnalysisProgress(0);
            return;
          }
          // Still processing — poll again
          setTimeout(poll, 1500);
        } catch {
          setTimeout(poll, 2000);
        }
      };
      setTimeout(poll, 1000);
    } catch (err) {
      toast.error(formatApiError(err));
      setAnalyzing(false);
    }
  };

  // Video time tracking
  useEffect(() => {
    if (!videoRef.current) return;
    const handler = () => {
      const t = videoRef.current?.currentTime || 0;
      setCurrentTime(t);
      // Auto-detect which scene we're in
      const idx = scenes.findIndex(
        (s) => t >= s.start_time && t <= s.end_time
      );
      if (idx >= 0 && idx !== currentScene) setCurrentScene(idx);
    };
    videoRef.current.addEventListener("timeupdate", handler);
    return () => videoRef.current?.removeEventListener("timeupdate", handler);
  }, [scenes, currentScene, project]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play();
    setPlaying(!playing);
  };

  const seekTo = (t) => {
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const seekToScene = (idx) => {
    setCurrentScene(idx);
    if (scenes[idx]) seekTo(scenes[idx].start_time);
  };

  // Scene editing
  const updateSceneTime = async (sceneId, field, value) => {
    try {
      await axios.put(`${API}/projects/${project.id}/scenes/${sceneId}`, {
        [field]: parseFloat(value),
      });
      setScenes((prev) =>
        prev.map((s) =>
          s.id === sceneId ? { ...s, [field]: parseFloat(value) } : s
        )
      );
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addScene = async () => {
    try {
      const lastScene = scenes[scenes.length - 1];
      const startTime = lastScene ? lastScene.end_time : 0;
      const endTime = Math.min(startTime + 2, duration);
      const res = await axios.post(`${API}/projects/${project.id}/scenes`, {
        start_time: startTime,
        end_time: endTime,
        subjects: [],
      });
      setScenes(sortByTime([...scenes, res.data]));
      toast.success("Scene added");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeScene = async (sceneId) => {
    try {
      await axios.delete(`${API}/projects/${project.id}/scenes/${sceneId}`);
      setScenes((prev) => prev.filter((s) => s.id !== sceneId));
      toast.success("Scene removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Crop position update
  const updateCropPosition = (sceneId, ar, axis, value) => {
    setCropPositions((prev) => ({
      ...prev,
      [sceneId]: {
        ...prev[sceneId],
        [ar]: { ...(prev[sceneId]?.[ar] || { x: 50, y: 50 }), [axis]: value },
      },
    }));
  };

  // Save crop positions and proceed
  const saveCropsAndContinue = async () => {
    setSavingCrops(true);
    try {
      // Build bulk positions payload from current state
      const positions = [];
      scenes.forEach((s) => {
        selectedAspects.forEach((ar) => {
          const pos = cropPositions[s.id]?.[ar] || { x: 50, y: 50 };
          const payload = {
            scene_id: s.id,
            aspect_ratio: ar,
            center_x: pos.x / 100,
            center_y: pos.y / 100,
          };
          if (pos.keyframes?.length) {
            payload.keyframes = pos.keyframes.map((kf) => ({
              time: kf.time,
              x: kf.x / 100,
              y: kf.y / 100,
            }));
          }
          positions.push(payload);
        });
      });
      // Bulk save all per-scene crop positions
      await axios.put(`${API}/projects/${project.id}/crop-positions-bulk`, { positions });
      // Also save aspect ratio config
      await axios.post(`${API}/projects/${project.id}/aspect-ratios`, {
        project_id: project.id,
        aspect_ratios: selectedAspects,
        mode: "automated",
      });
      toast.success("Per-scene crop positions saved");
      setCurrentStep(4);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingCrops(false);
    }
  };

  // ─── TIMELINE HANDLERS ───
  const handleTimelineSplit = async (sceneIndex, splitTime) => {
    const scene = scenes[sceneIndex];
    try {
      const res = await axios.post(
        `${API}/projects/${project.id}/scenes/${scene.id}/split`,
        { split_time: splitTime }
      );
      setScenes((prev) => {
        const newScenes = [...prev];
        newScenes[sceneIndex] = res.data.original;
        newScenes.splice(sceneIndex + 1, 0, res.data.new_scene);
        return newScenes;
      });
      // Copy crop positions from original scene to new scene
      setCropPositions((prev) => ({
        ...prev,
        [res.data.new_scene.id]: { ...(prev[scene.id] || {}) },
      }));
      toast.success("Scene split");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleTimelineTrim = (sceneIndex, field, value, persist) => {
    const roundedValue = parseFloat(value.toFixed(2));
    setScenes((prev) =>
      prev.map((s, i) =>
        i === sceneIndex ? { ...s, [field]: roundedValue } : s
      )
    );
    if (persist && scenes[sceneIndex]) {
      updateSceneTime(scenes[sceneIndex].id, field, roundedValue);
    }
  };

  const handleTimelineDelete = (sceneIndex) => {
    if (scenes.length <= 1) return;
    removeScene(scenes[sceneIndex].id);
    if (currentScene >= sceneIndex && currentScene > 0) {
      setCurrentScene(currentScene - 1);
    }
  };


  // ─── KEYFRAME HANDLERS ───
  const getActiveKeyframes = () => {
    const scene = scenes[currentScene];
    if (!scene || !activeAspect) return [];
    return cropPositions[scene.id]?.[activeAspect]?.keyframes || [];
  };

  const findKeyframeAtTime = (kfs, time) =>
    kfs.findIndex((kf) => Math.abs(kf.time - time) < KEYFRAME_TOLERANCE);

  const getInterpolatedPosition = () => {
    const scene = scenes[currentScene];
    if (!scene || !activeAspect) return { x: 50, y: 50 };
    const data = cropPositions[scene.id]?.[activeAspect];
    if (!data) return { x: 50, y: 50 };
    if (data.keyframes?.length >= 2) {
      const interp = interpolateKeyframes(data.keyframes, currentTime);
      if (interp) return interp;
    }
    return { x: data.x, y: data.y };
  };

  const addKeyframe = async () => {
    const scene = scenes[currentScene];
    if (!scene || !activeAspect) return;
    const data = cropPositions[scene.id]?.[activeAspect] || { x: 50, y: 50, keyframes: [] };
    const kfs = [...(data.keyframes || [])];
    const existIdx = findKeyframeAtTime(kfs, currentTime);
    if (existIdx >= 0) {
      toast.info("Keyframe already exists at this time");
      return;
    }
    // Get current interpolated position as the new keyframe value
    const pos = getInterpolatedPosition();
    kfs.push({ time: parseFloat(currentTime.toFixed(2)), x: Math.round(pos.x), y: Math.round(pos.y) });
    kfs.sort((a, b) => a.time - b.time);

    setCropPositions((prev) => ({
      ...prev,
      [scene.id]: {
        ...prev[scene.id],
        [activeAspect]: { ...data, keyframes: kfs },
      },
    }));

    try {
      await axios.put(
        `${API}/projects/${project.id}/scenes/${scene.id}/keyframes`,
        { aspect_ratio: activeAspect, keyframes: kfs.map((kf) => ({ time: kf.time, x: kf.x / 100, y: kf.y / 100 })) }
      );
      toast.success("Keyframe added");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeKeyframe = async (kfIndex) => {
    const scene = scenes[currentScene];
    if (!scene || !activeAspect) return;
    const data = cropPositions[scene.id]?.[activeAspect];
    if (!data?.keyframes) return;
    const kfs = data.keyframes.filter((_, i) => i !== kfIndex);

    setCropPositions((prev) => ({
      ...prev,
      [scene.id]: {
        ...prev[scene.id],
        [activeAspect]: { ...data, keyframes: kfs },
      },
    }));

    try {
      await axios.put(
        `${API}/projects/${project.id}/scenes/${scene.id}/keyframes`,
        { aspect_ratio: activeAspect, keyframes: kfs.map((kf) => ({ time: kf.time, x: kf.x / 100, y: kf.y / 100 })) }
      );
      toast.success("Keyframe removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Update crop position — now keyframe-aware
  const updateCropPositionKF = (sceneId, ar, axis, value) => {
    setCropPositions((prev) => {
      const data = prev[sceneId]?.[ar] || { x: 50, y: 50, keyframes: [] };
      const kfs = [...(data.keyframes || [])];
      const kfIdx = findKeyframeAtTime(kfs, currentTime);
      if (kfIdx >= 0) {
        kfs[kfIdx] = { ...kfs[kfIdx], [axis]: value };
      }
      return {
        ...prev,
        [sceneId]: {
          ...prev[sceneId],
          [ar]: {
            ...data,
            [axis]: value,
            keyframes: kfs,
          },
        },
      };
    });
  };

  // Save keyframes after slider change (debounced via onPointerUp)
  const persistKeyframes = async () => {
    const scene = scenes[currentScene];
    if (!scene || !activeAspect) return;
    const data = cropPositions[scene.id]?.[activeAspect];
    if (!data?.keyframes?.length) return;
    try {
      await axios.put(
        `${API}/projects/${project.id}/scenes/${scene.id}/keyframes`,
        { aspect_ratio: activeAspect, keyframes: data.keyframes.map((kf) => ({ time: kf.time, x: kf.x / 100, y: kf.y / 100 })) }
      );
    } catch {}
  };

  // Build keyframe time map for timeline display
  const sceneKeyframes = {};
  scenes.forEach((s) => {
    const kfs = activeAspect && cropPositions[s.id]?.[activeAspect]?.keyframes;
    if (kfs?.length) sceneKeyframes[s.id] = kfs;
  });


  // ─── OVERLAY HANDLERS ───
  const handleOverlayMove = async (overlayId, newStart, newEnd, persist) => {
    setOverlays((prev) =>
      prev.map((o) =>
        o.id === overlayId ? { ...o, start_time: newStart, end_time: newEnd } : o
      )
    );
    if (persist) {
      try {
        await axios.put(`${API}/projects/${project.id}/graphics/${overlayId}`, {
          start_time: newStart,
          end_time: newEnd,
        });
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
  };

  const handleOverlayTrim = async (overlayId, field, value, persist) => {
    setOverlays((prev) =>
      prev.map((o) => (o.id === overlayId ? { ...o, [field]: value } : o))
    );
    if (persist) {
      try {
        await axios.put(`${API}/projects/${project.id}/graphics/${overlayId}`, {
          [field]: value,
        });
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
  };

  const handleOverlayDelete = async (overlayId) => {
    try {
      await axios.delete(`${API}/projects/${project.id}/graphics/${overlayId}`);
      setOverlays((prev) => prev.filter((o) => o.id !== overlayId));
      if (selectedOverlay === overlayId) setSelectedOverlay(null);
      toast.success("Overlay removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addOverlayToTimeline = async (type = "super") => {
    try {
      const startT = parseFloat(currentTime.toFixed(2));
      const endT = parseFloat(Math.min(startT + 3, duration).toFixed(2));
      const res = await axios.post(`${API}/projects/${project.id}/graphics`, {
        project_id: project.id,
        type,
        content: type === "super" ? "New Text" : type === "disclaimer" ? "Disclaimer" : "End Frame",
        start_time: startT,
        end_time: endT,
        position: { x: 50, y: 85 },
        style: {},
      });
      const newOverlay = res.data;
      if (newOverlay._id) delete newOverlay._id;
      setOverlays((prev) => [...prev, newOverlay]);
      setSelectedOverlay(newOverlay.id);
      toast.success(`${type.replace("_", " ")} added to timeline`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };


  // ─── REVIEW LINK ───
  const generateReviewLink = async () => {
    if (!activeAspect || !project) return;
    try {
      const res = await axios.post(`${API}/projects/${project.id}/review-links`, {
        aspect_ratio: activeAspect,
      });
      const origin = window.location.origin;
      const link = `${origin}/review/${res.data.token}`;
      setReviewLink(link);
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Review link copied to clipboard!");
      } catch {
        toast.success("Review link generated!");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };


  // Calculate crop overlay style
  const getCropStyle = (ar) => {
    const [w, h] = ar.split(":").map(Number);
    const ratio = w / h;
    const srcW = project?.video_width || 1920;
    const srcH = project?.video_height || 1080;
    const srcRatio = srcW / srcH;
    let ow, oh;
    if (ratio > srcRatio) {
      ow = 100;
      oh = (srcRatio / ratio) * 100;
    } else {
      oh = 100;
      ow = (ratio / srcRatio) * 100;
    }
    const scene = scenes[currentScene];
    const cropData = scene && cropPositions[scene.id]?.[ar];
    let pos = cropData ? { x: cropData.x, y: cropData.y } : { x: 50, y: 50 };
    // Interpolate from keyframes if available
    if (cropData?.keyframes?.length >= 2) {
      const interp = interpolateKeyframes(cropData.keyframes, currentTime);
      if (interp) pos = interp;
    }
    return {
      width: `${ow}%`,
      height: `${oh}%`,
      left: `${(100 - ow) * (pos.x / 100)}%`,
      top: `${(100 - oh) * (pos.y / 100)}%`,
    };
  };

  const { videoUrl: videoStreamUrl } = useVideoUrl(
    project?.id, "source", !!project?.source_video
  );

  // ─── RENDER ──────────────────────────────

  return (
    <div className="min-h-screen" data-testid="crop-workspace-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      {/* Header */}
      <header className="border-b border-cyan-500/10 backdrop-blur-sm bg-[#020a14]/60 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button
              onClick={() =>
                currentStep > 1 && !project
                  ? setCurrentStep(currentStep - 1)
                  : navigate(project ? `/project/${project.id}` : "/dashboard")
              }
              className="text-cyan-400/40 hover:text-cyan-300 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-['Rajdhani'] tracking-wide text-sm lg:text-base">
                ALTERNATIVE ASPECT RATIO RESIZING / CROPPING
              </h1>
              {project && (
                <p className="text-xs text-cyan-200/30">
                  {project.name}
                  {editTitle ? ` - ${editTitle}` : ""}
                </p>
              )}
            </div>
          </div>

          {/* Step indicators */}
          <div className="hidden md:flex items-center gap-1">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs transition-all ${
                    currentStep === step.id
                      ? "bg-cyan-500/15 border border-cyan-500/30 text-cyan-300"
                      : currentStep > step.id
                      ? "text-cyan-400/60"
                      : "text-cyan-500/20"
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <span className="font-mono">{step.id}</span>
                  )}
                  <span className="font-['Rajdhani'] tracking-wider hidden lg:inline">
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`w-6 h-px mx-1 ${
                      currentStep > step.id
                        ? "bg-cyan-500/40"
                        : "bg-cyan-500/10"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Save */}
          {project && (
            <button
              onClick={() =>
                toast.success("Project saved - you can resume anytime")
              }
              className="text-sm text-cyan-400/60 hover:text-cyan-300 flex items-center gap-2 transition-colors"
              data-testid="save-progress-btn"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">Save</span>
            </button>
          )}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 lg:py-12">
        {/* ─── STEP 1: PROJECT SETUP ─── */}
        {currentStep === 1 && (
          <div className="max-w-xl mx-auto" data-testid="step-1">
            <div className="text-center mb-12">
              <span className="text-xs text-cyan-400/60 tracking-widest uppercase">
                Step 1 of 4
              </span>
              <h2 className="text-3xl font-['Rajdhani'] tracking-wide mt-3">
                PROJECT SETUP
              </h2>
              <p className="text-cyan-200/40 mt-2">
                Organise your work into projects for easy access
              </p>
            </div>

            <div className="space-y-6">
              {/* Project mode selection */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setProjectMode("existing")}
                  className={`p-6 border rounded-sm text-left transition-all ${
                    projectMode === "existing"
                      ? "border-cyan-500/40 bg-cyan-500/[0.05] shadow-[0_0_15px_rgba(0,180,255,0.1)]"
                      : "border-cyan-500/10 hover:border-cyan-500/20"
                  }`}
                  disabled={existingProjects.length === 0}
                  data-testid="mode-existing"
                >
                  <p className="font-['Rajdhani'] tracking-wide mb-1">
                    ADD TO EXISTING PROJECT
                  </p>
                  <p className="text-xs text-cyan-200/30">
                    {existingProjects.length} project
                    {existingProjects.length !== 1 ? "s" : ""} available
                  </p>
                </button>
                <button
                  onClick={() => setProjectMode("new")}
                  className={`p-6 border rounded-sm text-left transition-all ${
                    projectMode === "new"
                      ? "border-cyan-500/40 bg-cyan-500/[0.05] shadow-[0_0_15px_rgba(0,180,255,0.1)]"
                      : "border-cyan-500/10 hover:border-cyan-500/20"
                  }`}
                  data-testid="mode-new"
                >
                  <p className="font-['Rajdhani'] tracking-wide mb-1">
                    START A NEW PROJECT
                  </p>
                  <p className="text-xs text-cyan-200/30">
                    Create a fresh project
                  </p>
                </button>
              </div>

              {/* Existing project selector */}
              {projectMode === "existing" && (
                <div className="space-y-3">
                  <Label className="text-sm text-cyan-300/60">
                    Select Project
                  </Label>
                  <Select
                    value={selectedProjectId}
                    onValueChange={setSelectedProjectId}
                  >
                    <SelectTrigger
                      className="h-12 rounded-sm"
                      data-testid="existing-project-select"
                    >
                      <SelectValue placeholder="Choose a project..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#020a14] border-cyan-500/20 rounded-sm">
                      {existingProjects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* New project form */}
              {projectMode === "new" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-cyan-300/60">
                      Project Title
                    </Label>
                    <Input
                      placeholder="e.g., Brand X Summer Campaign 2026"
                      value={newProjectTitle}
                      onChange={(e) => setNewProjectTitle(e.target.value)}
                      className="h-12 rounded-sm"
                      data-testid="new-project-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-cyan-300/60">
                      Edit Title / Key Number{" "}
                      <span className="text-cyan-500/30">(Optional)</span>
                    </Label>
                    <Input
                      placeholder="e.g., TVC 30s - Summer Hero Edit"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="h-12 rounded-sm"
                      data-testid="edit-title"
                    />
                    <p className="text-xs text-cyan-200/20">
                      Useful when a campaign has multiple TVC edits to resize
                    </p>
                  </div>
                </div>
              )}

              {projectMode && (
                <Button
                  onClick={handleProjectSetup}
                  className="w-full h-12 rounded-sm btn-primary font-['Rajdhani'] tracking-wider"
                  disabled={creatingProject}
                  data-testid="step1-next"
                >
                  {creatingProject ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      NEXT <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ─── STEP 2: ASSETS ─── */}
        {currentStep === 2 && (
          <div className="max-w-2xl mx-auto" data-testid="step-2">
            <div className="text-center mb-12">
              <span className="text-xs text-cyan-400/60 tracking-widest uppercase">
                Step 2 of 4
              </span>
              <h2 className="text-3xl font-['Rajdhani'] tracking-wide mt-3">
                ASSETS
              </h2>
              <p className="text-cyan-200/40 mt-2">
                Select your target aspect ratios and upload your source video
              </p>
            </div>

            <div className="space-y-10">
              {/* Aspect Ratios */}
              <div>
                <h3 className="text-sm text-cyan-400/60 mb-4 tracking-widest uppercase">
                  Aspect Ratios
                </h3>
                <p className="text-xs text-cyan-200/20 mb-4">
                  Select the alternative aspect ratios you need. You can add as
                  many as required.
                </p>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {DEFAULT_ASPECTS.map((a) => (
                    <label
                      key={a.id}
                      className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer transition-all ${
                        selectedAspects.includes(a.id)
                          ? "border-cyan-500/40 bg-cyan-500/[0.05] shadow-[0_0_15px_rgba(0,180,255,0.1)]"
                          : "border-cyan-500/10 hover:border-cyan-500/20"
                      }`}
                      data-testid={`aspect-${a.id}`}
                    >
                      <Checkbox
                        checked={selectedAspects.includes(a.id)}
                        onCheckedChange={() => toggleAspect(a.id)}
                      />
                      <div>
                        <p className="font-['Rajdhani'] tracking-wide text-lg">
                          {a.label}
                        </p>
                        <p className="text-xs text-cyan-200/30">{a.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Custom aspect ratio */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Custom ratio e.g. 3:2"
                    value={customAspect}
                    onChange={(e) => setCustomAspect(e.target.value)}
                    className="h-10 rounded-sm"
                    data-testid="custom-ratio-input"
                  />
                  <Button
                    variant="outline"
                    onClick={addCustomAspect}
                    className="h-10 rounded-sm btn-outline-blue"
                    data-testid="add-custom-ratio"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>

                {/* Show selected custom aspects */}
                {selectedAspects.filter(
                  (a) => !DEFAULT_ASPECTS.find((d) => d.id === a)
                ).length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-3">
                    {selectedAspects
                      .filter((a) => !DEFAULT_ASPECTS.find((d) => d.id === a))
                      .map((a) => (
                        <span
                          key={a}
                          className="px-3 py-1 text-sm bg-cyan-500/10 border border-cyan-500/20 rounded-sm text-cyan-200/60 flex items-center gap-2"
                        >
                          {a}
                          <button
                            onClick={() => toggleAspect(a)}
                            className="text-cyan-400/40 hover:text-red-400"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </div>

              {/* Upload */}
              <div>
                <h3 className="text-sm text-cyan-400/60 mb-4 tracking-widest uppercase">
                  Upload Source Video
                </h3>

                {project?.source_video ? (
                  <div className="card-glass rounded-sm p-6 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-sm bg-cyan-500/10 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-cyan-200/80">Video uploaded</p>
                      <p className="text-xs text-cyan-200/30">
                        {project.video_info?.width}x{project.video_info?.height}{" "}
                        - {Math.round(project.video_duration || 0)}s -{" "}
                        {project.video_info?.codec?.toUpperCase()}
                      </p>
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-cyan-400/40 hover:text-cyan-300"
                    >
                      Replace
                    </button>
                  </div>
                ) : (
                  <div
                    className="drop-zone rounded-sm p-12 flex flex-col items-center justify-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-zone"
                  >
                    {uploading ? (
                      <div className="text-center w-64">
                        <Loader2 className="w-8 h-8 mx-auto text-cyan-400/40 animate-spin mb-4" />
                        <p className="text-cyan-200/40 text-sm mb-4">
                          Uploading... {uploadProgress}%
                        </p>
                        <Progress value={uploadProgress} className="h-1" />
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-cyan-500/30 mb-4" />
                        <p className="text-cyan-200/60 mb-1">
                          Click to upload your source TVC video
                        </p>
                        <p className="text-sm text-cyan-200/30">
                          MOV, MP4, AVI, ProRes
                        </p>
                      </>
                    )}
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*,.mov,.mp4,.avi"
                  onChange={handleUpload}
                  className="hidden"
                />

                {/* Tips */}
                <div className="mt-4 space-y-3">
                  <div className="flex gap-3 p-3 border border-amber-500/15 rounded-sm bg-amber-500/[0.03]">
                    <AlertCircle className="w-4 h-4 text-amber-400/60 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-200/50 leading-relaxed">
                      <strong className="text-amber-200/70">Important:</strong>{" "}
                      Please upload a "superless submaster" (a version of your
                      TVC without burnt-in supers / graphics) for best results.
                    </p>
                  </div>
                  <div className="flex gap-3 p-3 border border-cyan-500/10 rounded-sm">
                    <Info className="w-4 h-4 text-cyan-400/40 shrink-0 mt-0.5" />
                    <p className="text-xs text-cyan-200/30 leading-relaxed">
                      <strong className="text-cyan-200/50">Tip:</strong> A high
                      resolution MP4 superless submaster is more than adequate as
                      a source file for social media deliverables, and will
                      process significantly faster than a ProRes 4444 MOV.
                    </p>
                  </div>
                </div>
              </div>

              {/* Next button */}
              <Button
                onClick={handleAssetsNext}
                className="w-full h-12 rounded-sm btn-primary font-['Rajdhani'] tracking-wider"
                disabled={
                  selectedAspects.length === 0 || !project?.source_video
                }
                data-testid="step2-next"
              >
                NEXT <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ─── STEP 3: CROP WORKSPACE ─── */}
        {currentStep === 3 && (
          <div data-testid="step-3">
            {/* Analysis trigger */}
            {!analysisComplete && !analyzing && (
              <div className="max-w-xl mx-auto text-center mb-12">
                <div className="card-glass rounded-sm p-12">
                  <Crop className="w-12 h-12 mx-auto text-cyan-400/30 mb-6" />
                  <h3 className="text-xl font-['Rajdhani'] tracking-wide mb-3">
                    READY TO ANALYSE
                  </h3>
                  <p className="text-cyan-200/40 mb-8 leading-relaxed">
                    MasteringMate will use AI to analyse your TVC video,
                    detecting scene cut points and primary subjects to
                    automatically position the aspect ratio crops.
                  </p>
                  <Button
                    onClick={runAnalysis}
                    className="h-12 px-8 rounded-sm btn-primary font-['Rajdhani'] tracking-wider"
                    data-testid="run-analysis-btn"
                  >
                    ANALYSE VIDEO
                  </Button>
                </div>
              </div>
            )}

            {analyzing && (
              <div className="max-w-xl mx-auto text-center mb-12">
                <div className="card-glass rounded-sm p-12">
                  <Loader2 className="w-10 h-10 mx-auto text-cyan-400/40 animate-spin mb-6" />
                  <h3 className="text-xl font-['Rajdhani'] tracking-wide mb-3">
                    ANALYSING VIDEO
                  </h3>
                  <p className="text-cyan-200/40 mb-6">
                    {analysisProgress < 25 ? "Detecting scene cuts..." :
                     analysisProgress < 50 ? "Extracting keyframes..." :
                     analysisProgress < 95 ? "AI subject detection..." :
                     "Finalising results..."}
                  </p>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-cyan-500/10 rounded-full overflow-hidden mb-3" data-testid="analysis-progress-bar">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400 rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${analysisProgress}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-cyan-200/40 font-mono">
                    <span data-testid="analysis-progress-pct">{analysisProgress}%</span>
                    <span data-testid="analysis-eta">
                      {(() => {
                        if (!analysisStartTime || analysisProgress < 5) return "Estimating...";
                        const elapsed = (Date.now() - analysisStartTime) / 1000;
                        const remaining = analysisProgress > 0 ? (elapsed / analysisProgress) * (100 - analysisProgress) : 0;
                        if (remaining < 5) return "Almost done";
                        if (remaining < 60) return `~${Math.ceil(remaining)}s remaining`;
                        return `~${Math.ceil(remaining / 60)}m remaining`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Main workspace */}
            {analysisComplete && (
              <div>
                {/* Top section: Aspect ratios + Video preview */}
                <div className="grid lg:grid-cols-4 gap-8 mb-10">
                  {/* Left: Select Ratios */}
                  <div className="space-y-4">
                    <h3 className="text-sm text-cyan-400/60 tracking-widest uppercase">
                      Select Ratios
                    </h3>
                    <div className="space-y-2">
                      {DEFAULT_ASPECTS.map((ar) => (
                        <div
                          key={ar.id}
                          onClick={() => {
                            toggleAspect(ar.id);
                            if (!activeAspect) setActiveAspect(ar.id);
                          }}
                          className={`w-full flex items-center gap-3 p-3 rounded-sm border transition-all text-left cursor-pointer ${
                            selectedAspects.includes(ar.id)
                              ? "border-cyan-500/30 bg-cyan-500/[0.06]"
                              : "border-cyan-500/10 hover:border-cyan-500/20"
                          }`}
                          data-testid={`ratio-select-${ar.id}`}
                        >
                          <Checkbox
                            checked={selectedAspects.includes(ar.id)}
                            className="border-cyan-500/30 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-500"
                          />
                          <div>
                            <span className="text-sm text-cyan-200/70 font-['Rajdhani'] tracking-wide">{ar.label}</span>
                            <p className="text-[10px] text-cyan-200/30">{ar.desc}</p>
                          </div>
                          <span className="ml-auto text-[10px] text-cyan-500/30 font-mono">{ar.label}</span>
                        </div>
                      ))}
                    </div>
                    {/* Custom ratio input */}
                    <div className="flex gap-2">
                      <Input
                        value={customAspect}
                        onChange={(e) => setCustomAspect(e.target.value)}
                        placeholder="Custom e.g. 3:2"
                        className="h-10 rounded-sm text-sm"
                        data-testid="custom-ratio-input"
                      />
                      <Button
                        onClick={addCustomAspect}
                        variant="outline"
                        className="h-10 px-3 rounded-sm border-cyan-500/20"
                        data-testid="add-custom-ratio"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Active aspect tabs */}
                    {selectedAspects.length > 0 && (
                      <div>
                        <h3 className="text-xs text-cyan-400/40 mb-2 tracking-widest uppercase mt-4">
                          Active Crop Preview
                        </h3>
                        <div className="flex flex-wrap gap-1">
                          {selectedAspects.map((ar) => (
                            <button
                              key={ar}
                              onClick={() => setActiveAspect(ar)}
                              className={`px-3 py-1.5 rounded-sm text-xs font-['Rajdhani'] tracking-wide transition-all ${
                                activeAspect === ar
                                  ? "bg-cyan-500/20 border border-cyan-500/40 text-cyan-300"
                                  : "border border-cyan-500/10 text-cyan-200/40 hover:text-cyan-200/60"
                              }`}
                              data-testid={`crop-tab-${ar}`}
                            >
                              {ar}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Video preview */}
                  <div className="lg:col-span-3 space-y-4">
                    <div className="aspect-video video-frame rounded-sm relative overflow-hidden">
                      {videoStreamUrl ? (
                        <>
                          <video
                            ref={videoRef}
                            src={videoStreamUrl}
                            className="w-full h-full object-contain bg-black"
                            playsInline
                            onPlay={() => setPlaying(true)}
                            onPause={() => setPlaying(false)}
                            onLoadedMetadata={() => {
                              if (videoRef.current) setDuration(videoRef.current.duration);
                            }}
                            data-testid="workspace-video"
                          />
                          {activeAspect && (
                            <div className="crop-overlay" style={getCropStyle(activeAspect)} data-testid={`active-crop-${activeAspect}`}>
                              <span className="absolute -top-5 left-1 text-xs text-cyan-400/80 font-mono bg-[#020a14]/80 px-1.5 py-0.5 rounded-sm">{activeAspect}</span>
                            </div>
                          )}
                          {selectedAspects.filter((ar) => ar !== activeAspect).map((ar) => (
                            <div key={ar} className="absolute border border-cyan-500/15 pointer-events-none" style={getCropStyle(ar)}>
                              <span className="absolute -top-4 left-1 text-[10px] text-cyan-500/30 font-mono">{ar}</span>
                            </div>
                          ))}
                          <div className="absolute top-3 right-3 bg-[#020a14]/80 px-2.5 py-1 rounded-sm text-xs text-cyan-200/60">
                            Scene {currentScene + 1} / {scenes.length}
                          </div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="w-8 h-8 text-cyan-400/30 animate-spin" />
                        </div>
                      )}
                    </div>

                    {/* Playback controls removed — now in Timeline below */}
                  </div>
                </div>

                {/* ── INTERACTIVE TIMELINE ── */}
                <TimelineTrack
                  scenes={scenes}
                  duration={duration}
                  currentTime={currentTime}
                  currentScene={currentScene}
                  playing={playing}
                  onTogglePlay={togglePlay}
                  onSeek={seekTo}
                  onSceneSelect={seekToScene}
                  onSplit={handleTimelineSplit}
                  onDelete={handleTimelineDelete}
                  onTrim={handleTimelineTrim}
                  onAddScene={addScene}
                  onAddKeyframe={addKeyframe}
                  sceneKeyframes={sceneKeyframes}
                  overlays={overlays}
                  selectedOverlay={selectedOverlay}
                  onOverlaySelect={setSelectedOverlay}
                  onOverlayMove={handleOverlayMove}
                  onOverlayTrim={handleOverlayTrim}
                  onOverlayDelete={handleOverlayDelete}
                  onAddOverlay={addOverlayToTimeline}
                  onPreview={activeAspect && scenes.length > 0 ? () => setShowPreview(true) : undefined}
                />

                {/* ── SELECTED SCENE CROP CONTROLS ── */}
                {scenes[currentScene] && (
                  <div className="border border-cyan-500/10 rounded-sm bg-cyan-500/[0.02] p-4 mt-4" data-testid="scene-crop-panel">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-cyan-500/40 font-mono">
                          {String(currentScene + 1).padStart(2, "0")}
                        </span>
                        <span className="text-sm text-cyan-200/70 font-['Rajdhani'] tracking-wide">
                          SCENE {currentScene + 1}
                        </span>
                        <span className="text-[10px] text-cyan-200/25 font-mono">
                          {scenes[currentScene].start_time.toFixed(1)}s — {scenes[currentScene].end_time.toFixed(1)}s
                          ({(scenes[currentScene].end_time - scenes[currentScene].start_time).toFixed(1)}s)
                        </span>
                      </div>
                      {/* Crop Mode toggle */}
                      <div className="inline-flex rounded-sm overflow-hidden border border-cyan-500/15">
                        <button
                          onClick={() => {
                            setScenes((prev) =>
                              prev.map((s, idx) =>
                                idx === currentScene ? { ...s, _cropMode: "automated" } : s
                              )
                            );
                          }}
                          className={`px-4 py-1.5 text-[10px] font-['Rajdhani'] tracking-wider transition-all ${
                            (scenes[currentScene]._cropMode || "manual") === "automated"
                              ? "bg-cyan-500/20 text-cyan-300"
                              : "text-cyan-200/30 hover:text-cyan-200/50"
                          }`}
                          data-testid="crop-mode-auto"
                        >
                          AUTO
                        </button>
                        <button
                          onClick={() => {
                            setScenes((prev) =>
                              prev.map((s, idx) =>
                                idx === currentScene ? { ...s, _cropMode: "manual" } : s
                              )
                            );
                          }}
                          className={`px-4 py-1.5 text-[10px] font-['Rajdhani'] tracking-wider transition-all ${
                            (scenes[currentScene]._cropMode || "manual") === "manual"
                              ? "bg-cyan-500/20 text-cyan-300"
                              : "text-cyan-200/30 hover:text-cyan-200/50"
                          }`}
                          data-testid="crop-mode-manual"
                        >
                          MANUAL
                        </button>
                      </div>
                    </div>

                    {/* Manual Position sliders for selected scene — keyframe aware */}
                    {(scenes[currentScene]._cropMode || "manual") === "manual" && activeAspect && (() => {
                      const sceneId = scenes[currentScene].id;
                      const kfs = getActiveKeyframes();
                      const pos = getInterpolatedPosition();
                      const atKfIdx = findKeyframeAtTime(kfs, currentTime);
                      const atKeyframe = atKfIdx >= 0;
                      return (
                        <div className="space-y-3">
                          {/* Keyframe toolbar */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={addKeyframe}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-['Rajdhani'] tracking-wider border border-cyan-500/20 rounded-sm text-cyan-400/60 hover:text-cyan-300 hover:bg-cyan-500/10 transition-all"
                              data-testid="add-keyframe-btn"
                            >
                              <Plus className="w-3 h-3" /> ADD KEYFRAME
                            </button>
                            {kfs.length > 0 && (
                              <span className="text-[10px] text-cyan-500/30 font-mono">
                                {kfs.length} keyframe{kfs.length !== 1 ? "s" : ""}
                              </span>
                            )}
                            {/* Keyframe pills */}
                            {kfs.map((kf, ki) => {
                              const isActive = Math.abs(kf.time - currentTime) < KEYFRAME_TOLERANCE;
                              return (
                                <div
                                  key={ki}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] font-mono cursor-pointer transition-all ${
                                    isActive
                                      ? "bg-amber-500/20 border border-amber-400/40 text-amber-300"
                                      : "bg-cyan-500/[0.05] border border-cyan-500/10 text-cyan-400/40 hover:text-cyan-300"
                                  }`}
                                  onClick={() => seekTo(kf.time)}
                                  data-testid={`keyframe-pill-${ki}`}
                                >
                                  <span className="w-2 h-2 rotate-45 bg-current inline-block" />
                                  {kf.time.toFixed(1)}s
                                  <button
                                    onClick={(e) => { e.stopPropagation(); removeKeyframe(ki); }}
                                    className="ml-1 text-red-400/40 hover:text-red-400"
                                  >
                                    <Minus className="w-2.5 h-2.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>

                          {/* Position sliders */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="p-3 rounded-sm bg-cyan-500/[0.03] border border-cyan-500/10">
                              <Label className="text-xs text-cyan-300/50 block mb-2">
                                Horizontal: {Math.round(pos.x)}%
                                {atKeyframe && <span className="ml-1 text-amber-400/50 text-[9px]">(keyframe)</span>}
                              </Label>
                              <Slider
                                value={[Math.round(pos.x)]}
                                onValueChange={([v]) => {
                                  if (kfs.length >= 2) {
                                    updateCropPositionKF(sceneId, activeAspect, "x", v);
                                  } else {
                                    updateCropPosition(sceneId, activeAspect, "x", v);
                                  }
                                }}
                                onPointerUp={persistKeyframes}
                                max={100}
                                data-testid="crop-x-slider"
                              />
                            </div>
                            <div className="p-3 rounded-sm bg-cyan-500/[0.03] border border-cyan-500/10">
                              <Label className="text-xs text-cyan-300/50 block mb-2">
                                Vertical: {Math.round(pos.y)}%
                                {atKeyframe && <span className="ml-1 text-amber-400/50 text-[9px]">(keyframe)</span>}
                              </Label>
                              <Slider
                                value={[Math.round(pos.y)]}
                                onValueChange={([v]) => {
                                  if (kfs.length >= 2) {
                                    updateCropPositionKF(sceneId, activeAspect, "y", v);
                                  } else {
                                    updateCropPosition(sceneId, activeAspect, "y", v);
                                  }
                                }}
                                onPointerUp={persistKeyframes}
                                max={100}
                                data-testid="crop-y-slider"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center justify-between pt-8 mt-8 border-t border-cyan-500/10">
                  <button onClick={() => setCurrentStep(2)} className="text-sm text-cyan-400/40 hover:text-cyan-300 flex items-center gap-2 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back to Assets
                  </button>
                  <div className="flex items-center gap-3">
                    {activeAspect && scenes.length > 0 && (
                      <button
                        onClick={generateReviewLink}
                        className="h-12 px-5 rounded-sm border border-violet-500/30 bg-violet-500/[0.06] text-violet-300 hover:bg-violet-500/15 font-['Rajdhani'] tracking-wider text-sm flex items-center gap-2 transition-all"
                        data-testid="share-review-btn"
                      >
                        <Link2 className="w-4 h-4" /> CLIENT REVIEW
                      </button>
                    )}
                    {activeAspect && scenes.length > 0 && (
                      <button
                        onClick={() => setShowPreview(true)}
                        className="h-12 px-6 rounded-sm border border-cyan-500/30 bg-cyan-500/[0.06] text-cyan-300 hover:bg-cyan-500/15 font-['Rajdhani'] tracking-wider text-sm flex items-center gap-2 transition-all"
                        data-testid="preview-btn"
                      >
                        <Eye className="w-4 h-4" /> PREVIEW
                      </button>
                    )}
                    <Button onClick={saveCropsAndContinue} className="h-12 px-8 rounded-sm btn-primary font-['Rajdhani'] tracking-wider" disabled={savingCrops} data-testid="step3-next">
                      {savingCrops ? <Loader2 className="w-4 h-4 animate-spin" /> : <>SAVE & CONTINUE <ArrowRight className="w-4 h-4 ml-2" /></>}
                    </Button>
                  </div>
                </div>

                {/* Review link display */}
                {reviewLink && (
                  <div className="mt-4 p-4 rounded-sm bg-violet-500/[0.06] border border-violet-500/20" data-testid="review-link-display">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-violet-300/60 font-['Rajdhani'] tracking-wider mb-1">CLIENT REVIEW LINK</p>
                        <p className="text-sm text-violet-200/80 font-mono break-all">{reviewLink}</p>
                      </div>
                      <button
                        onClick={() => { navigator.clipboard.writeText(reviewLink); toast.success("Copied!"); }}
                        className="ml-4 p-2 rounded-sm border border-violet-500/20 text-violet-300/60 hover:text-violet-200 hover:bg-violet-500/10 transition-all shrink-0"
                        data-testid="copy-review-link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-[10px] text-violet-300/30 mt-2">Clients can review and approve crops without logging in. Link expires in 14 days.</p>
                  </div>
                )}

                {/* Preview Modal */}
                {showPreview && videoStreamUrl && (
                  <PreviewPlayer
                    videoUrl={videoStreamUrl}
                    scenes={scenes}
                    cropPositions={cropPositions}
                    activeAspect={activeAspect}
                    overlays={overlays}
                    duration={duration}
                    projectId={project.id}
                    sourceWidth={project.video_width}
                    sourceHeight={project.video_height}
                    onClose={() => setShowPreview(false)}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 4: CONTINUE ─── */}
        {currentStep === 4 && project && (
          <ContinueStep project={project} />
        )}
      </main>
    </div>
  );
};

export default CropWorkspacePage;
